// Pushing the outbox: a port of `SyncEngine.pushLocked` / `process` (GroundTeam/Sync/SyncEngine.swift:211-408),
// WEB_PLAN.md §3.6.
//
// - Order: saves before deletes, then the collection order (parents first: a new record goes before its `created`
//   activity), then id (SyncEngine.pushOrder).
// - Batches of at most 200 items and 1.5 MB of payload JSON: a big batch holds the company's advisory lock and blocks
//   every phone's push.
// - Per item, at most 3 sends per push (conflict → merge → retry). Like iOS, an item that still conflicts after that
//   stays pending for the next push (the plan's "then mark failed" is not what the Swift engine does).

import { COLLECTION_ORDER, FILE_COLLECTIONS, type Collection } from '@/contracts/collections';
import { isUuid } from '@/contracts/uuid';
import type { PushItem, PushResult, Row } from '@/contracts/wire';
import { toEnvelopeDate } from '@/contracts/wireDate';
import { SyncApiError, isSyncApiError } from './api';
import { canonicalPayload, keepsMergeBase } from './canonical';
import { deepEqual, threeWay } from './merge';
import type { CachedRow, OutboxEntry } from './replica';
import { itemKey, localIsNewer, toCachedRow, type SyncRuntime } from './runtime';

export const PUSH_BATCH_LIMIT = 200;
export const PUSH_BATCH_BYTES = 1_500_000;
export const MAX_ATTEMPTS_PER_PUSH = 3;

export const FORBIDDEN_MESSAGE = "You don't have permission to change that, so it was restored.";
export const LAST_ADMIN_MESSAGE = 'The company must keep at least one active admin.';

/** Something the person should hear about (a toast); produced by push, surfaced by the engine. */
export type SyncNotice =
  | { kind: 'deletedElsewhere'; collection: Collection; id: string; message: string }
  | { kind: 'forbidden'; collection: Collection; id: string; message: string }
  | { kind: 'lastAdmin'; collection: Collection; id: string; message: string }
  | { kind: 'invalid'; collection: Collection; id: string; message: string }
  | { kind: 'rejected'; collection: Collection; id: string; message: string };

export interface PushOutcome {
  /** Items the server saved (including confirmed deletes). */
  saved: number;
  /** Items sent, counting retries. */
  sent: number;
  /** Calls made to sync_push. */
  calls: number;
  notices: SyncNotice[];
  stopped: boolean;
}

export interface PushHooks {
  /** Called right before each sync_push call with the batch (tests use it to edit "during flight"). */
  onBatch?: (items: readonly PushItem[]) => void;
}

/** SyncEngine.pushOrder. */
export function pushOrder<T extends Pick<OutboxEntry, 'collection' | 'id' | 'kind'>>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) => {
    const da = a.kind === 'delete';
    const db = b.kind === 'delete';
    if (da !== db) return da ? 1 : -1;
    const ra = COLLECTION_ORDER[a.collection] ?? 0;
    const rb = COLLECTION_ORDER[b.collection] ?? 0;
    if (ra !== rb) return ra - rb;
    const ia = a.id.toUpperCase();
    const ib = b.id.toUpperCase();
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
}

/** UTF-8 byte length without allocating (what the payload weighs on the wire). */
export function utf8Length(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i++;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

/** The `sync_push` item for an entry (§3.6 step 3). */
export function buildPushItem(rt: SyncRuntime, entry: OutboxEntry): PushItem {
  const row = rt.replica.getRow(entry.collection, entry.id);
  const item: PushItem = {
    collection: entry.collection,
    id: entry.id,
    payload: entry.kind === 'save' ? entry.localPayload : null,
    modified_at: entry.modifiedAt,
    modified_by: rt.replica.deviceId,
    deleted: entry.kind === 'delete',
    base_version: entry.baseVersion,
  };
  const filePath = entry.filePath ?? row?.filePath;
  if (filePath && FILE_COLLECTIONS.includes(entry.collection)) item.file_path = filePath;
  return item;
}

interface Sent {
  item: PushItem;
  rev: number;
}

/**
 * Pushes every pending entry. Throws the API error when a whole call fails (network, auth, not_member/suspended);
 * entries stay queued. Returns what happened for the engine's status and toasts.
 */
export async function pushOnce(rt: SyncRuntime, hooks: PushHooks = {}): Promise<PushOutcome> {
  const replica = rt.replica;
  const outcome: PushOutcome = { saved: 0, sent: 0, calls: 0, notices: [], stopped: false };
  const attempts = new Map<string, number>();
  const skipped = new Set<string>();

  while (!rt.isStopped()) {
    const candidates = replica.entries().filter((e) => {
      const key = itemKey(e.collection, e.id);
      return e.state === 'pending' && !skipped.has(key) && (attempts.get(key) ?? 0) < MAX_ATTEMPTS_PER_PUSH;
    });
    if (candidates.length === 0) break;

    const batch: PushItem[] = [];
    const sent = new Map<string, Sent>();
    let bytes = 0;
    for (const entry of pushOrder(candidates)) {
      if (batch.length >= PUSH_BATCH_LIMIT) break;
      const item = buildPushItem(rt, entry);
      const size = item.payload ? utf8Length(JSON.stringify(item.payload)) : 0;
      if (batch.length > 0 && bytes + size > PUSH_BATCH_BYTES) break;
      const key = itemKey(entry.collection, entry.id);
      attempts.set(key, (attempts.get(key) ?? 0) + 1);
      batch.push(item);
      sent.set(key, { item, rev: entry.rev });
      bytes += size;
      replica.putEntry({ ...entry, attempts: entry.attempts + 1 });
    }
    if (batch.length === 0) break;

    hooks.onBatch?.(batch);
    outcome.calls++;
    const result = await rt.api.push(rt.companyId, batch);
    if (rt.isStopped()) break;
    outcome.sent += batch.length;
    await processResult(rt, result, sent, skipped, outcome);
    if (rt.isStopped()) break;
    await replica.persist();
  }
  if (rt.isStopped()) outcome.stopped = true;
  return outcome;
}

async function processResult(
  rt: SyncRuntime,
  result: PushResult,
  sent: Map<string, Sent>,
  skipped: Set<string>,
  outcome: PushOutcome,
): Promise<void> {
  const replica = rt.replica;

  // Saved: the pushed payload is the new server copy and merge base.
  for (const saved of result.saved) {
    const key = itemKey(saved.collection, saved.id);
    const s = sent.get(key);
    if (!s) continue;
    outcome.saved++;
    const { item, rev } = s;
    const entry = replica.getEntry(item.collection, item.id);
    if (saved.deleted || item.deleted) {
      replica.removeRow(item.collection, item.id);
      if (entry) replica.removeEntry(item.collection, item.id);
      continue;
    }
    const previous = replica.getRow(item.collection, item.id);
    const row: CachedRow = {
      id: previous?.id ?? item.id.toUpperCase(),
      payload: item.payload!,
      version: saved.version,
      serverSeq: saved.server_seq,
      modifiedAt: item.modified_at,
      modifiedBy: item.modified_by,
    };
    const filePath = item.file_path ?? previous?.filePath;
    if (filePath) row.filePath = filePath;
    replica.putRow(item.collection, row);
    if (!entry) continue; // discarded while in flight: the server has it anyway
    if (entry.rev === rev) {
      replica.removeEntry(item.collection, item.id);
    } else {
      // Edited again after this batch was built: rebase that newer edit on the version just saved.
      replica.putEntry({
        ...entry,
        basePayload: keepsMergeBase(item.collection) ? item.payload : null,
        baseVersion: saved.version,
      });
    }
  }

  // Conflicts: the server moved on since our base version. Merge, then retry what is still ours.
  for (const row of result.conflicts) {
    const key = itemKey(row.collection, row.id);
    if (row.deleted) {
      replica.removeRow(row.collection, row.id);
      const had = replica.removeEntry(row.collection, row.id);
      if (had && sent.has(key)) outcome.notices.push(deletedElsewhere(row));
      continue;
    }
    const cached = toCachedRow(row);
    const entry = replica.getEntry(row.collection, row.id);
    if (!cached || !row.payload) {
      // The server copy has no readable payload: drop the local change instead of looping on it.
      if (entry) replica.removeEntry(row.collection, row.id);
      continue;
    }
    replica.putRow(row.collection, cached);
    if (!entry) continue;
    const server = canonicalPayload(row.collection, row.payload);
    const base = keepsMergeBase(row.collection) ? server : null;
    if (entry.kind === 'delete') {
      // Deletes win: send it again against the current version.
      replica.putEntry({ ...entry, basePayload: base, baseVersion: row.version });
      continue;
    }
    const merged = threeWay(
      keepsMergeBase(row.collection) ? entry.basePayload : null,
      entry.localPayload ?? {},
      server,
      localIsNewer(entry.modifiedAt, row.modified_at),
    );
    if (deepEqual(merged, server)) {
      replica.removeEntry(row.collection, row.id);
    } else {
      replica.putEntry({
        ...entry,
        basePayload: base,
        baseVersion: row.version,
        localPayload: merged,
        modifiedAt: toEnvelopeDate(rt.now()),
      });
    }
  }

  // Per-item errors.
  const forbidden: { collection: Collection; id: string; lastAdmin: boolean }[] = [];
  for (const error of result.errors) {
    if (!error.collection || !isUuid(error.id)) continue;
    const { collection, id } = { collection: error.collection, id: error.id };
    const key = itemKey(collection, id);
    const entry = replica.getEntry(collection, id);
    const message = error.message.trim();
    switch (error.code) {
      case 'forbidden':
        forbidden.push({ collection, id, lastAdmin: message === 'last_admin' });
        break;
      case 'invalid': {
        // Kept as an unsynced local change: shown "Not saved" with the reason, never pruned, Discard offered.
        // If it was edited again while this batch was in flight, the newer edit gets its own try instead.
        const editedSince = entry !== undefined && sent.get(key) !== undefined && entry.rev !== sent.get(key)!.rev;
        if (entry && !editedSince) replica.putEntry({ ...entry, state: 'failed', error: message || 'The server rejected this change.' });
        else skipped.add(key);
        outcome.notices.push({ kind: 'invalid', collection, id: entry?.id ?? id, message: invalidText(message) });
        break;
      }
      default: {
        if (error.code === 'not_member' || error.code === 'suspended') {
          throw new SyncApiError(error.code, error.code, 403, '42501');
        }
        skipped.add(key);
        if (entry) replica.putEntry({ ...entry, error: message || error.code });
        outcome.notices.push({ kind: 'rejected', collection, id: entry?.id ?? id, message: message || error.code });
      }
    }
  }
  for (const f of forbidden) {
    const key = itemKey(f.collection, f.id);
    const entryId = replica.getEntry(f.collection, f.id)?.id ?? f.id;
    let row: Row | null;
    try {
      row = await rt.api.get(rt.companyId, f.collection, f.id);
    } catch (error) {
      if (rt.isStopped()) return;
      if (isSyncApiError(error) && error.isRemoval) throw error;
      skipped.add(key);
      continue;
    }
    if (rt.isStopped()) return;
    const cached = row ? toCachedRow(row) : null;
    if (cached) replica.putRow(f.collection, cached);
    else replica.removeRow(f.collection, f.id);
    replica.removeEntry(f.collection, f.id);
    outcome.notices.push(
      f.lastAdmin
        ? { kind: 'lastAdmin', collection: f.collection, id: entryId, message: LAST_ADMIN_MESSAGE }
        : { kind: 'forbidden', collection: f.collection, id: entryId, message: FORBIDDEN_MESSAGE },
    );
  }
}

function deletedElsewhere(row: Row): SyncNotice {
  const what = row.collection === 'records' ? 'This store' : 'This item';
  return { kind: 'deletedElsewhere', collection: row.collection, id: row.id.toUpperCase(), message: `${what} was deleted by someone else.` };
}

function invalidText(message: string): string {
  return message ? `The server didn't accept this change (${message}). It stays here as "Not saved".` : `The server didn't accept this change. It stays here as "Not saved".`;
}
